<template>
    <div id="detail">
        <div class="header">
            <div class="title">{{ $t("ticimax.automationRules") }}</div>
            <div class="subtitle">{{ $t("ticimax.createAutomationDesc") }}</div>
        </div>
        <div class="toolbox">
            <div class="left"></div>
            <div class="right">
                <SupsisButton primary @click="() => (createMode = true)">
                    <a-icon type="plus" />
                    {{ $t("ticimax.createAutomationRule") }}
                </SupsisButton>
            </div>
        </div>
        <CreateRule :channel="channel" v-if="createMode" @cancel="() => (createMode = false)" @ok="onRuleAdded" />
        <ve-table
            v-else
            :columns="getColumns()"
            :table-data="rules"
            :row-style-option="rowStyleOption"
            :cell-style-option="cellStyleOption"
            :border-x="true"
            :border-y="true"
            :expand-option="expandOption"
            row-key-field-name="_id"
            id="automation-rules-table"
            :fixed-header="true"
            style="word-break: break-word"
            :style="{
                width: getMaxWidth() + 'px',
            }"
        />
    </div>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import { TICIMAX_API } from "../../../../../../api/ticimax";
import MoneyFormatter from "../../../../../../components/MoneyFormatter.vue";
import log2 from "../../../../../../utils/log2";
import CreateRule from "./CreateRule.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import { RRule } from "rrule";
import { sendSocketMessage } from "../../../../../../utils/socketStream";
import { mapState } from "vuex";

const log = log2("AutomationRules");
export default {
    name: "AutomationRules",
    components: {
        MoneyFormatter,
        CreateRule,
        SupsisButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
        }),
    },
    data: function () {
        return {
            createMode: false,
            loadingInstance: null,
            rowStyleOption: {
                stripe: true,
            },
            cellStyleOption: {
                bodyCellClass: function ({ row, column, rowIndex }) {
                    const classNames = [column.key];

                    return classNames.join(" ");
                },
            },

            expandOption: {
                trigger: "row",
                render: ({ row, column, rowIndex }, h) => {
                    return (
                        <div>
                            <ve-table
                                columns={this.getHistoryTableColumns()}
                                table-data={row.history}
                                row-style-option={{
                                    stripe: true,
                                }}
                                border-x={true}
                                border-y={true}
                                row-key-field-name="_id"
                                id=" automation-rules-table"
                                fixed-header={true}
                                style={{
                                    "word-break": "break-word",
                                    width: this.getMaxWidth() + " px",
                                }}
                            />
                        </div>
                    );
                },
            },
            rules: [],
        };
    },
    methods: {
        getMaxWidth() {
            return window.innerWidth - 350;
        },
        onRuleAdded() {
            this.createMode = false;
            this.fetch();
        },
        deleteRule(id) {
            this.$confirm({
                title: "Are you sure delete this rule?",
                onOk: () => {
                    TICIMAX_API.deleteAutomationRule(id)
                        .then((res) => {
                            this.rules = this.rules.filter((x) => x._id !== id);
                            sendSocketMessage("automation ticimax-automation-added", {
                                siteId: this.siteId,
                            });
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                },
                onCancel() {},
            });
        },
        stopRule(id) {
            this.$confirm({
                title: "Are you sure stop this rule?",
                onOk: () => {
                    TICIMAX_API.stopAutomationRule(id)
                        .then((res) => {
                            sendSocketMessage("automation ticimax-automation-added", {
                                siteId: this.siteId,
                            });
                            this.fetch();
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                },
                onCancel() {},
            });
        },
        fetch() {
            log("fetch");
            this.loading = true;
            this.loadingInstance.show();
            TICIMAX_API.listAutomationRules()
                .then((res) => {
                    this.rules = res.data;
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    this.loading = false;
                    this.loadingInstance.close();
                });
        },
        getRRuleSummary(rruleString) {
            log("getRRuleSummary", rruleString);
            const rrule = RRule.fromString(rruleString);
            // const nextDate = rrule.after(moment().toDate(), true);
            // return rrule.toText() + " - Next: " + moment(nextDate).format("YYYY-MM-DD HH:mm");
            return rrule.toText();
        },
        getNextScheduledDate(rruleString) {
            log("getNextScheduledDate", rruleString);
            const rrule = RRule.fromString(rruleString);
            const nextDate = rrule.after(moment().toDate(), true);
            return moment(nextDate).format("YYYY-MM-DD HH:mm");
        },
        getSumOfTriggerredUserCount(row) {
            return _.sum(row.history.map((x) => x.sendUsers.length));
        },
        getStatus(row) {
            const statusList = row.history.map((x) => x.status);
            const isRunning = statusList.includes("running");
            const isActive = statusList.includes("scheduled");
            const cancelExists = statusList.includes("cancelled");
            if (isRunning) {
                return "running";
            }
            if (isActive) {
                return "active";
            }
            if (!isActive && cancelExists) {
                return "cancelled";
            }
        },
        getHistoryTableColumns() {
            return [
                {
                    field: "index",
                    key: "index",
                    operationColumn: true,
                    title: "#",
                    width: 15,
                    fixed: "left",
                },
                {
                    field: "rrule",
                    key: "rrule2",
                    title: "Date",
                    align: "left",
                    width: 200,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.runAt}</span>;
                    },
                },
                {
                    field: "totalTriggeredUser",
                    key: "totalTriggeredUser",
                    title: "Sended User Count",
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.sendUsers.length}</span>;
                    },
                },
                {
                    field: "status",
                    key: "status",
                    title: "Status",
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.status}</span>;
                    },
                },
            ];
        },
        getColumns() {
            console.log("getColumns", this.users);
            return [
                {
                    field: "",
                    key: "a",
                    type: "expand",
                    title: "",
                    width: 50,
                    align: "center",
                },
                {
                    field: "name",
                    key: "name",
                    title: this.$t("ticimax.automation"),
                    align: "left",
                    width: 100,
                },
                {
                    field: "rrule",
                    key: "rrule",
                    title: this.$t("ticimax.timerRule"),
                    align: "left",
                    width: 200,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{this.getRRuleSummary(row.rrule)}</span>;
                    },
                },
                {
                    field: "rrule",

                    key: "rrule",
                    title: this.$t("ticimax.nextScheduled"),

                    align: "left",
                    width: 200,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{this.getNextScheduledDate(row.rrule)}</span>;
                    },
                },
                {
                    field: "totalTriggeredUser",
                    key: "totalTriggeredUser",
                    title: this.$t("ticimax.triggeredUserCount"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{this.getSumOfTriggerredUserCount(row)}</span>;
                    },
                },
                {
                    field: "status",
                    key: "status",
                    title: this.$t("ticimax.status"),
                    align: "left",
                    width: 100,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{this.getStatus(row)}</span>;
                    },
                },
                {
                    field: "actions",
                    key: "actions",
                    title: this.$t("chatbotv2.Actions"),
                    align: "left",
                    width: 200,
                    operation: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div class="actions">
                                <SupsisButton ghost primary onClick={() => this.deleteRule(row._id)}>
                                    Delete
                                </SupsisButton>
                                <SupsisButton ghost primary onClick={() => this.stopRule(row._id)}>
                                    Stop
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector("#automation-rules-table"),
            name: "wave",
        });
        this.fetch();
    },
    watch: {},
};
</script>
<style lang="scss">
#automation-rules-table {
    .ve-table-body-td {
        &.segment {
            padding: 0px;
            font-weight: 700;
            font-size: 14px;
            text-transform: uppercase;

            span {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            &.active-30 {
                background-color: #67c23a;

                color: white;
            }

            &.active-60 {
                background-color: #e6a23c;
                color: white;
            }

            &.active-90 {
                background-color: #f56c6c;
                color: white;
            }

            &.lost {
                background-color: #909399;
                color: white;
            }
        }

        .actions {
            display: flex;
            gap: 5px;
            align-items: center;
        }
    }
}
</style>
<style scoped lang="scss">
.toolbox {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 10px;
    border-bottom: 1px solid var(--color-border-default);

    .right {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }
}

.header {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;
    padding-bottom: 10px;

    border-bottom: 1px solid var(--color-border-default);

    .title {
        font-size: 18px;
        font-weight: 700;
        color: var(--color-text-strong-default);
        padding-left: 5px;
    }

    .subtitle {
        font-size: 14px;
        color: var(--color-text-strong-default);
    }
}
</style>
